import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, Monitor, CheckCircle, XCircle, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SalesPage({ user }) {
  const [salesUrl, setSalesUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [popupWindow, setPopupWindow] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    fetchSalesUrl();
    
    // Check popup status periodically
    const interval = setInterval(() => {
      if (popupWindow && popupWindow.closed) {
        setIsPopupOpen(false);
        setPopupWindow(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [popupWindow]);

  const fetchSalesUrl = async () => {
    try {
      const response = await axios.get(`${API}/settings/sales-url`);
      setSalesUrl(response.data.url || "https://merchantwebportal.axis.bank.in/wp_axis/Master/LOGIN3.aspx");
    } catch (error) {
      console.error("Error fetching sales URL:", error);
      setSalesUrl("https://merchantwebportal.axis.bank.in/wp_axis/Master/LOGIN3.aspx");
    } finally {
      setIsLoading(false);
    }
  };

  const openPopup = useCallback(() => {
    // Calculate popup dimensions and position
    const width = Math.min(1200, window.screen.width - 100);
    const height = Math.min(800, window.screen.height - 100);
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Open popup window
    const popup = window.open(
      salesUrl,
      'POSSalesPortal',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=yes,scrollbars=yes,resizable=yes`
    );

    if (popup) {
      setPopupWindow(popup);
      setIsPopupOpen(true);
      popup.focus();
    } else {
      alert("Please allow popups for this site to open the POS portal.");
    }
  }, [salesUrl]);

  const focusPopup = () => {
    if (popupWindow && !popupWindow.closed) {
      popupWindow.focus();
    } else {
      openPopup();
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} activePath="/sales" />
      
      <main className="flex-1 p-6 lg:p-8 ml-0 lg:ml-64">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-foreground mb-2">POS Sales Portal</h1>
            <p className="text-muted-foreground">Access your Axis Bank POS sales dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            {isPopupOpen ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                POS Portal Open
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-full text-sm font-medium">
                <XCircle className="w-4 h-4" />
                POS Portal Closed
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <Card className="border-0 shadow-card overflow-hidden" data-testid="sales-container">
          <CardContent className="p-8">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="text-center py-12">
                {/* Portal Icon */}
                <div className="relative inline-block mb-8">
                  <div className="w-32 h-32 bg-gradient-to-br from-[#087DDF] to-[#0056b3] rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-[#087DDF]/30">
                    <Monitor className="w-16 h-16 text-white" />
                  </div>
                  {isPopupOpen && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                {/* Title */}
                <h2 className="font-heading text-2xl mb-3">Axis Bank POS Portal</h2>
                <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
                  Click the button below to open the POS sales portal in a dedicated window. 
                  You can keep this app open alongside for easy access.
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  {isPopupOpen ? (
                    <Button
                      size="lg"
                      className="h-14 px-8 rounded-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-500/30"
                      onClick={focusPopup}
                      data-testid="focus-popup-btn"
                    >
                      <Maximize2 className="w-5 h-5 mr-2" />
                      Bring POS Window to Front
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="h-14 px-8 rounded-full bg-gradient-to-r from-[#087DDF] to-[#0056b3] hover:opacity-90 shadow-lg shadow-[#087DDF]/30"
                      onClick={openPopup}
                      data-testid="open-popup-btn"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Open POS Sales Portal
                    </Button>
                  )}
                </div>

                {/* URL Info */}
                <div className="bg-muted/50 rounded-xl p-4 inline-block">
                  <p className="text-xs text-muted-foreground mb-1">Portal URL</p>
                  <code className="text-sm text-foreground break-all">{salesUrl}</code>
                </div>

                {/* Tips */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                  <div className="bg-[#F2337B]/5 rounded-xl p-4">
                    <div className="w-10 h-10 bg-[#F2337B]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#F2337B] font-bold">1</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Click "Open POS Sales Portal" to launch</p>
                  </div>
                  <div className="bg-[#F6D003]/10 rounded-xl p-4">
                    <div className="w-10 h-10 bg-[#F6D003]/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#b39700] font-bold">2</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Login with your Axis Bank credentials</p>
                  </div>
                  <div className="bg-[#087DDF]/5 rounded-xl p-4">
                    <div className="w-10 h-10 bg-[#087DDF]/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <span className="text-[#087DDF] font-bold">3</span>
                    </div>
                    <p className="text-sm text-muted-foreground">View your daily sales transactions</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
